import ReservationExternalStep1 from "@/modules/front/ReservationExternal/ReservationExternalStep1";
import React from "react";
import ReservationExternalStep2 from "@/modules/front/ReservationExternal/ReservationExternalStep2";
import FormProgress from "@/modules/front/ReservationExternal/FormProgress";
import moment from "moment/moment";
import OrderService from "@/modules/front/services/OrderService";
import ReservationExternalStepFinish from "@/modules/front/ReservationExternal/ReservationExternalStepFinish";
import ReservationExternalStepBankTransfer from "@/modules/front/ReservationExternal/ReservationExternalStepBankTransfer";
import ReservationExternalStepExtras from "@/modules/front/ReservationExternal/ReservationExternalStepExtras";
import ReservationExternalStepDelivery from "@/modules/front/ReservationExternal/ReservationExternalStepDelivery";
import ServiceService from "@/modules/front/services/ServiceService";
import ProductService from "@/modules/front/services/ProductService";

// Fakturační adresa firmy (nepovinná) — posílají se jen u rezervace na firmu
const pickCompanyBilling = (data) => data && data.company_name ? {
    company_address: data.company_address,
    company_city: data.company_city,
    company_zip: data.company_zip,
} : null;

const DELIVERY_METHOD_PICKUP = 'pickup';
const DELIVERY_METHOD_PACKETA = 'packeta';

// Iframe nemá vlastní dataLayer (viz ReservationIframe/@layout.latte), takže eventy
// posíláme postMessage rodičovské stránce – tam je zachytí www/reservation-external/script.js
// a napushuje je do window.dataLayer tenanta (stejný formát jako {event, ...args}
// v @layout.latte / get_events()).
const pushDataLayerEvent = (name, args) => {
    if (!window.parent || typeof window.parent.postMessage !== 'function') {
        return;
    }
    window.parent.postMessage({
        type: 'dataLayerEvent',
        payload: Object.assign({event: name}, args),
    }, '*');
};

const ReservationExternal = (props) => {
    const packetaEnabled = !!props.packetaEnabled;

    const [step, setStep] = React.useState('personalInfo');
    const [error, setError] = React.useState(null);

    React.useEffect(() => {
        pushDataLayerEvent('view_item', {
            currency: 'CZK',
            items: [{
                item_id: props.itemId,
                item_name: props.itemTitle,
                quantity: 1,
            }],
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Widget běží v iframe (viz ReservationIframe/default.latte). Po přechodu na jiný
    // krok se výška obsahu změní a rodičovská stránka iframe zvětší/zmenší, ale sama
    // od sebe nescrolluje – bez tohoto by nový krok mohl zůstat mimo viditelnou oblast.
    // Výšku i scroll řeší jedna společná zpráva (notifyReservationStepChange v
    // ReservationIframe/default.latte), aby se scroll nepočítal podle staré výšky.
    const isFirstStepRender = React.useRef(true);
    React.useEffect(() => {
        if (isFirstStepRender.current) {
            isFirstStepRender.current = false;
            return;
        }
        if (typeof window.notifyReservationStepChange === 'function') {
            window.notifyReservationStepChange();
        }
    }, [step]);

    const [dateFrom, setDateFrom] = React.useState(null);
    const [dateTo, setDateTo] = React.useState(null);
    const [totalPrice, setTotalPrice] = React.useState(null);
    const [itemPrice, setItemPrice] = React.useState(null);
    const [depositTotal, setDepositTotal] = React.useState(null);
    const [vatMode, setVatMode] = React.useState(null);
    const [totalPriceVat, setTotalPriceVat] = React.useState(null);

    const pricingNote = '';

    const [personalData, setPersonalData] = React.useState({});
    const [selectedServices, setSelectedServices] = React.useState([]);

    const [selectedItems, setSelectedItems] = React.useState([]);

    const [delivery, setDelivery] = React.useState({method: null, point: null});

    const [submitting, setSubmitting] = React.useState(false);
    const [hasExtras, setHasExtras] = React.useState(false);
    const [bankTransfer, setBankTransfer] = React.useState(null);

    // Existence doplňků (services i recommended items) jde zjistit nezávisle na
    // vybraném termínu – recommended-items endpoint bez dateFrom/dateTo vrací
    // nakonfigurované položky bez filtru dostupnosti. Krok "Doplňky" se tak
    // v progressbaru zobrazí od začátku, ne až po výběru termínu v kroku 1.
    React.useEffect(() => {
        Promise.all([
            ServiceService.getAll(window.variables.entityId),
            ProductService.getRecommendedItems(window.variables.entityId)
        ]).then(([services, recommendedItems]) => {
            const itemsArray = Object.keys(recommendedItems).map((key) => recommendedItems[key]);
            if ((services && services.length > 0) || (itemsArray && itemsArray.length > 0)) {
                setHasExtras(true);
            }
        }).catch(() => {});
    }, []);

    // Ordered list of steps actually shown to the customer. Extras/delivery are optional
    // and only appear when relevant (has add-ons available / Packeta integration enabled).
    const stepKeys = ['personalInfo', hasExtras && 'extras', packetaEnabled && 'delivery', 'summary'].filter(Boolean);

    const getNextStepKey = (fromKey) => {
        const index = stepKeys.indexOf(fromKey);
        return index >= 0 && index < stepKeys.length - 1 ? stepKeys[index + 1] : stepKeys[stepKeys.length - 1];
    };

    const getPreviousStepKey = (fromKey) => {
        const index = stepKeys.indexOf(fromKey);
        return index > 0 ? stepKeys[index - 1] : stepKeys[0];
    };

    // Hlavní položka z kroku 1 (kalendář) může být zároveň nabídnutá jako
    // příslušenství v kroku "extras" (typicky "přidej druhý kus"). V takovém
    // případě její quantity v selectedItems představuje CELKOVÝ počet kusů
    // včetně toho, co je už vybráno v kalendáři – proto se nepřičítá zvlášť.
    const buildProductIds = (items) => {
        const entityId = window.variables.entityId;
        const mainSelection = items.find((item) => String(item.id) === String(entityId));
        const mainQuantity = mainSelection ? Math.max(1, mainSelection.quantity || 1) : 1;
        const otherExpanded = items
            .filter((item) => String(item.id) !== String(entityId))
            .flatMap((item) => Array(item.quantity || 1).fill(item.id));
        return [...Array(mainQuantity).fill(entityId), ...otherExpanded];
    };

    const getServicesForOrder = (services, deliveryState) => {
        if (deliveryState && deliveryState.method === DELIVERY_METHOD_PACKETA) {
            return [...services, {
                id: null,
                name: window.translations.reservationExternal.delivery.packeta,
                price: props.packetaPrice || 0,
                quantity: 1,
            }];
        }
        return services;
    };

    const createOrderWithDelivery = (data, services, deliveryState, placeOrder) => {
        return OrderService.createOrder(
            data.firstname,
            data.lastname,
            data.email,
            data.phone,
            dateFrom,
            dateTo,
            buildProductIds(selectedItems),
            getServicesForOrder(services, deliveryState),
            placeOrder,
            data.address,
            data.city,
            data.zip,
            data.company_name,
            data.company_id,
            data.company_vat_id,
            data.customValues,
            deliveryState ? deliveryState.method : null,
            deliveryState && deliveryState.point ? deliveryState.point.id : null,
            deliveryState && deliveryState.point ? deliveryState.point.name : null,
            deliveryState && deliveryState.point ? deliveryState.point : null,
            props.locale,
            pickCompanyBilling(data),
        );
    };

    const handleSelectedServices = (serviceSelections, services, itemIds, items) => {
        // serviceSelections = [{id, quantity}]
        let newSelectedServices = selectedServices.filter((selectedService) => {
            return selectedService.id === undefined;
        });

        const serviceMap = {};
        services.forEach((service) => { serviceMap[service.id] = service; });

        serviceSelections.forEach(({id, quantity}) => {
            const service = serviceMap[id];
            if (service && quantity > 0) {
                newSelectedServices.push({
                    id: service.id,
                    name: service.name,
                    price: service.price,
                    quantity
                });
            }
        });

        setSelectedServices(newSelectedServices);

        // itemIds is [{id, quantity}]
        const itemMap = {};
        items.forEach((item) => { itemMap[item.id] = item; });
        let newSelectedItems = [];
        itemIds.forEach(({id, quantity}) => {
            if (itemMap[id] && quantity > 0) {
                newSelectedItems.push({...itemMap[id], quantity});
            }
        });
        setSelectedItems(newSelectedItems);

        OrderService.createOrder(
            personalData.firstname,
            personalData.lastname,
            personalData.email,
            personalData.phone,
            dateFrom,
            dateTo,
            buildProductIds(newSelectedItems),
            getServicesForOrder(newSelectedServices, delivery),
            false,
            personalData.address,
            personalData.city,
            personalData.zip,
            personalData.company_name,
            personalData.company_id,
            personalData.company_vat_id,
            personalData.customValues,
            delivery.method,
            delivery.point ? delivery.point.id : null,
            delivery.point ? delivery.point.name : null,
            delivery.point,
            props.locale,
            pickCompanyBilling(personalData),
        ).then((response) => {
            if (response.error) {
                setError(response.error)
            }
            else {
                setTotalPrice(response.totalPrice);
                setItemPrice(response.itemPrice);
                setDepositTotal(response.depositTotal);
                setVatMode(response.vatMode);
                setTotalPriceVat(response.totalPriceVat);
                setError(false);
            }
        });
    }

    const handleDateConfirmed = (dateFrom, dateTo, officeId) => {
        setDateFrom(dateFrom);
        setDateTo(dateTo);
        setError(null);

        Promise.all([
            ServiceService.getAll(window.variables.entityId),
            ProductService.getRecommendedItems(window.variables.entityId, dateFrom, dateTo)
        ]).then(([services, recommendedItems]) => {
            const itemsArray = Object.keys(recommendedItems).map((key) => recommendedItems[key]);
            const hasAnyExtras = (services && services.length > 0) || (itemsArray && itemsArray.length > 0);
            setHasExtras((prev) => prev || hasAnyExtras);
        }).catch(() => {});
    }

    const handlePersonalData = (data) => {
        setPersonalData(data);
        createOrderWithDelivery(data, selectedServices, delivery, false)
            .then((response) => {
                if (response.error) {
                    setError(response.error)
                }
                else {
                    setStep(getNextStepKey('personalInfo'));
                    setTotalPrice(response.totalPrice);
                    setItemPrice(response.itemPrice);
                    setDepositTotal(response.depositTotal);
                    setVatMode(response.vatMode);
                    setTotalPriceVat(response.totalPriceVat);
                    setError(null);

                    pushDataLayerEvent('add_to_cart', {
                        currency: 'CZK',
                        value: response.totalPrice,
                        items: [{
                            item_id: window.variables.entityId,
                            item_name: props.itemTitle,
                            quantity: 1,
                            price: response.itemPrice,
                        }],
                    });
                }
            });

    }

    const handleDeliveryConfirmed = (deliveryState) => {
        setDelivery(deliveryState);
        createOrderWithDelivery(personalData, selectedServices, deliveryState, false)
            .then((response) => {
                if (response.error) {
                    setError(response.error);
                }
                else {
                    setTotalPrice(response.totalPrice);
                    setItemPrice(response.itemPrice);
                    setDepositTotal(response.depositTotal);
                    setVatMode(response.vatMode);
                    setTotalPriceVat(response.totalPriceVat);
                    setError(null);
                    setStep(getNextStepKey('delivery'));
                }
            });
    }

    const handleSubmit = () => {
        setSubmitting(true);
        createOrderWithDelivery(personalData, selectedServices, delivery, true)
            .then((response) => {
            setSubmitting(false);
            if (response.error) {
                setError(response.error)
            }
            else {
                if (response.orderId) {
                    pushDataLayerEvent('purchase', {
                        currency: 'CZK',
                        value: response.totalPrice,
                        transaction_id: response.orderId,
                        order_id: response.orderId,
                        items: [
                            ...buildProductIds(selectedItems).map((id) => ({
                                item_id: id,
                                item_name: id === window.variables.entityId ? props.itemTitle : undefined,
                                quantity: 1,
                            })),
                            ...getServicesForOrder(selectedServices, delivery).map((service) => ({
                                item_id: service.id,
                                item_name: service.name,
                                quantity: service.quantity || 1,
                                price: service.price,
                            })),
                        ],
                    });

                    if (response.bankTransfer) {
                        setBankTransfer(response.bankTransfer);
                        setStep('bankTransfer');
                    } else {
                        setStep('finish');
                    }
                }
                else {
                    setError("Něco se pokazilo, zkuste to prosím znovu.")
                }
            }
        });
    }

    const stepLabels = stepKeys.map((key) => window.translations.reservationExternal.steps[key]);
    const currentStepForProgress = stepKeys.indexOf(step) >= 0 ? stepKeys.indexOf(step) + 1 : null;

    let stepContent = null;
    switch (step) {
        case 'personalInfo':
            stepContent = <ReservationExternalStep1 setStep={setStep}
                                                    onPersonalDataSubmitted={handlePersonalData}
                                                    onDateConfirmed={handleDateConfirmed}
                                                    onError={setError}
                                                    dateFrom={dateFrom}
                                                    dateTo={dateTo}
                                                    personalData={personalData}
                                                    pricingNote={pricingNote}
                                                    customFields={props.customFields || []}
                                                    {...props}/>;
            break;
        case 'extras':
            stepContent = <ReservationExternalStepExtras setSelectedServices={handleSelectedServices}
                                                         dateFrom={dateFrom}
                                                         dateTo={dateTo}
                                                         itemId={window.variables.entityId}
                                                         pricingNote={pricingNote}
                                                         onBack={() => setStep(getPreviousStepKey('extras'))}
                                                         onNext={() => setStep(getNextStepKey('extras'))}
                                                         {...props}/>;
            break;
        case 'delivery':
            stepContent = <ReservationExternalStepDelivery delivery={delivery}
                                                            packetaWidgetApiKey={props.packetaWidgetApiKey}
                                                            packetaPrice={props.packetaPrice}
                                                            packetaCountry={props.packetaCountry}
                                                            onBack={() => setStep(getPreviousStepKey('delivery'))}
                                                            onNext={handleDeliveryConfirmed}
                                                            onError={setError}
                                                            {...props}/>;
            break;
        case 'summary':
            stepContent = <ReservationExternalStep2 setStep={setStep}
                                                    personalData={personalData}
                                                    dateFrom={dateFrom}
                                                    dateTo={dateTo}
                                                    totalPrice={totalPrice}
                                                    depositTotal={depositTotal}
                                                    vatMode={vatMode}
                                                    totalPriceVat={totalPriceVat}
                                                    itemTitle={props.itemTitle}
                                                    itemPrice={itemPrice}
                                                    onSubmit={handleSubmit}
                                                    onDateConfirmed={handleDateConfirmed}
                                                    selectedServices={getServicesForOrder(selectedServices, delivery)}
                                                    selectedItems={selectedItems}
                                                    delivery={delivery}
                                                    onErrors={setError}
                                                    submitting={submitting}
                                                    pricingNote={pricingNote}
                                                    hasExtras={hasExtras}
                                                    onBack={() => setStep(getPreviousStepKey('summary'))}
                                                    {...props}/>;
            break;
        case 'bankTransfer':
            stepContent = <ReservationExternalStepBankTransfer bankTransfer={bankTransfer} />
            break;
        case 'finish':
            stepContent = <ReservationExternalStepFinish />
            break;
    }

    let errorBox = null;
    if (error) {
        errorBox = <div className="alert alert-danger" role="alert">
            {error}
        </div>;
    }

    return <div>
        {errorBox}
        {currentStepForProgress && <FormProgress steps={stepLabels} currentStep={currentStepForProgress}/>}
        {stepContent}
    </div>

};

export default ReservationExternal;
